import React, { useEffect, useState } from 'react';
import { apiClient } from '../utils/const';
import { usePermissions } from './PermissionContext';

const Dashboard = () => {
  const { hasPermission, permissions } = usePermissions();
  const [dashboardData, setDashboardData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [schemeType, setSchemeType] = useState(localStorage.getItem("schemeType") || "mandal");

  // Listen for scheme changes
  useEffect(() => {
    const handleSchemeChange = () => {
      const currentScheme = localStorage.getItem("schemeType") || "mandal";
      setSchemeType(currentScheme);
    };

    window.addEventListener("schemeChanged", handleSchemeChange);
    handleSchemeChange();

    return () => {
      window.removeEventListener("schemeChanged", handleSchemeChange);
    };
  }, []);

  useEffect(() => {
    const fetchDashboardData = async () => {
      const isFirstLoad = !dashboardData;
      if (isFirstLoad) setLoading(true);
      setError(null);

      try {
        const response = await apiClient.post('/get_dashboard.php');
        if (response.data.message === 'radha') {
          const schemeData = response.data.data[schemeType];
          setDashboardData(schemeData);
        } else {
          setError(response.data.message || 'Failed to fetch dashboard data');
        }
      } catch (err) {
        console.error('Dashboard API error:', err);
        setError('Failed to load dashboard data');
      } finally {
        setLoading(false);
      }
    };

    fetchDashboardData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [schemeType]);

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(amount);
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-IN', {
      day: '2-digit',
      month: 'short',
      year: 'numeric'
    });
  };

  if (loading) {
    return (
      <div className="page-wrapper">
        <div className="page-content">
          <div className="container-xxl">
            <div className="row">
              <div className="col-12">
                <div className="card">
                  <div className="card-body text-center p-5">
                    <div className="spinner-border text-primary" role="status">
                      <span className="visually-hidden">Loading...</span>
                    </div>
                    <p className="mt-3 mb-0">Loading dashboard data...</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="page-wrapper">
        <div className="page-content">
          <div className="container-xxl">
            <div className="row">
              <div className="col-12">
                <div className="card">
                  <div className="card-body text-center p-5">
                    <div className="alert alert-danger" role="alert">
                      <i className="fas fa-exclamation-triangle me-2"></i>
                      {error}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="page-wrapper" style={{ height: '100vh', overflow: 'hidden' }}>
      <div className="page-content" style={{ height: '100%', overflow: 'hidden' }}>
        <div className="container-xxl" style={{ height: '100%', overflow: 'auto' }}>
          {/* Header */}
          <div className="row mb-1">
            <div className="col-12">
              <div className="d-flex justify-content-between align-items-center bg-body p-1 rounded shadow-sm">
                <h2 className="mb-0 fw-bold">
                  {schemeType === "kanchan" ? "Kanchanvarsha" : "Mandal"} • Dashboard
                </h2>
              </div>
            </div>
          </div>

          {/* Stats Row */}
          <div className="row">
            {/* Overall Stats */}
            <div className="col-12 col-md-6" style={{ height: '295px' }}>
              <div className="card border-0 shadow-sm">
                <div className="card-header ">
                  <h5 className="card-title mb-0 fw-bold">Overall Stats</h5>
                </div>
                <div className="card-body p-4">
                  <div className="row g-4">
                    <div className="col-6">
                      <div className="text-center">
                        <h4 className="mb-1 fw-bold" style={{ color: 'rgb(15, 118, 110)', fontSize: '1.5rem' }}>
                          ₹{Number(dashboardData?.today_total_transaction || 0).toLocaleString('en-IN')}
                        </h4>
                        <h6 className="mb-0 fw-normal" style={{ color: 'var(--bs-secondary-color)' }}>Today Total</h6>
                      </div>
                    </div>
                    <div className="col-6">
                      <div className="text-center">
                        <h4 className="mb-1 fw-bold" style={{ color: 'rgb(15, 118, 110)', fontSize: '1.5rem' }}>
                          ₹{Number(dashboardData?.total_installment || 0).toLocaleString('en-IN')}
                        </h4>
                        <h6 className="mb-0 fw-normal" style={{ color: 'var(--bs-secondary-color)' }}>Total Installment</h6>
                      </div>
                    </div>
                    <div className="col-6">
                      <div className="text-center">
                        <h4 className="mb-1 fw-bold" style={{ color: 'rgb(15, 118, 110)', fontSize: '1.5rem' }}>
                          ₹{Number(dashboardData?.total_withdraw || 0).toLocaleString('en-IN')}
                        </h4>
                        <h6 className="mb-0 fw-normal" style={{ color: 'var(--bs-secondary-color)' }}>Total Withdraw</h6>
                      </div>
                    </div>
                    <div className="col-6">
                      <div className="text-center">
                        <h4 className="mb-1 fw-bold" style={{ color: 'rgb(15, 118, 110)', fontSize: '1.5rem' }}>
                          ₹{Number(dashboardData?.in_hand || 0).toLocaleString('en-IN')}
                        </h4>
                        <h6 className="mb-0 fw-normal" style={{ color: 'var(--bs-secondary-color)' }}>In Hand</h6>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Active User Stats */}
            <div className="col-12 col-md-6" style={{ maxHeight: '295px' }}>
              <div className="card border-0 shadow-sm">
                <div className="card-header ">
                  <h5 className="card-title mb-0 fw-bold">Active User Stats</h5>
                </div>
                <div className="card-body p-4">
                  <div className="row g-4">
                  <div className="col-6">
                      <div className="text-center">
                        <h4 className="mb-1 fw-bold" style={{ color: 'rgb(15, 118, 110)', fontSize: '1.5rem' }}>
                          {dashboardData?.total_users || 0}
                        </h4>
                        <h6 className="mb-0 fw-normal" style={{ color: 'var(--bs-secondary-color)' }}>Total Users</h6>
                      </div>
                    </div>
                    <div className="col-6">
                      <div className="text-center">
                        <h4 className="mb-1 fw-bold" style={{ color: 'rgb(15, 118, 110)', fontSize: '1.5rem' }}>
                          {dashboardData?.total_active_users || 0}
                        </h4>
                        <h6 className="mb-0 fw-normal" style={{ color: 'var(--bs-secondary-color)' }}>Active Users</h6>
                      </div>
                    </div>
                    <div className="col-6 ">
                      <div className="text-center">
                        <h4 className="mb-1 fw-bold" style={{ color: 'rgb(15, 118, 110)', fontSize: '1.5rem' }}>
                          ₹{Number(dashboardData?.total_active_installments || 0)}
                        </h4>
                        <h6 className="mb-0 fw-normal" style={{ color: 'var(--bs-secondary-color)' }}>Total Installments</h6>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Div 2 - Recent Transactions (Full Width) */}
          <div className="row justify-content-center">
            <div className="col-12">
              <div className="card border-0 shadow-sm" style={{ height: '295px', borderRadius: '12px', boxShadow: '0 4px 20px rgba(0,0,0,0.1)'}}>
                <div className="card-header">
                  <h5 className="card-title fw-bold ">Recent Transactions</h5>
                </div>
                <div className="card-body" style={{  overflowY: 'auto' }}>
                  {dashboardData?.today_transaction && dashboardData.today_transaction.length > 0 ? (
                    <div className="table-responsive" style={{ border: '1px solid var(--bs-border-color)', borderRadius: '12px', overflow: 'hidden' }}>
                      <table className="table align-middle mb-0" style={{ borderCollapse: 'separate', borderSpacing: '0' }}>
                        <thead className="table-light">
                          <tr>
                            <th className="text-center px-3 py-2" style={{ border: 'none', fontWeight: 'bold', fontSize: '0.9rem' }}>User</th>
                            <th className="text-center px-3 py-2" style={{ border: 'none', fontWeight: 'bold', fontSize: '0.9rem' }}>Amount</th>
                            <th className="text-center px-3 py-2" style={{ border: 'none', fontWeight: 'bold', fontSize: '0.9rem' }}>Type</th>
                            <th className="text-center px-3 py-2" style={{ border: 'none', fontWeight: 'bold', fontSize: '0.9rem' }}>Date</th>
                          </tr>
                        </thead>
                        <tbody>
                          {dashboardData.today_transaction.map((transaction, index) => (
                            <tr
                              key={index}
                              style={{
                                transition: "all 0.3s ease",
                                backgroundColor: "transparent",
                                border: "none"
                              }}
                              onMouseEnter={(e) => {
                                const tr = e.target.closest("tr");
                                tr.style.backgroundColor = "var(--bs-primary-bg-subtle)";
                                tr.style.transform = "translateY(-1px)";
                              }}
                              onMouseLeave={(e) => {
                                const tr = e.target.closest("tr");
                                tr.style.backgroundColor = "transparent";
                                tr.style.transform = "translateY(0px)";
                              }}
                            >
                              {/* User Column */}
                              <td className="text-center px-3 py-2" style={{ border: 'none' }}>
                                <span
                                  className="badge bg-primary bg-gradient px-2 py-1"
                                  style={{
                                    fontSize: "0.8rem",
                                    fontWeight: "600",
                                    borderRadius: "8px",
                                  }}
                                >
                                  #{transaction.card_no}
                                </span>
                              </td>

                              {/* Amount Column */}
                              <td className="text-center px-3 py-2" style={{ border: 'none' }}>
                                <span
                                  className="badge bg-success bg-gradient px-3 py-2"
                                  style={{
                                    fontSize: "0.85rem",
                                    fontWeight: "600",
                                    borderRadius: "12px",
                                    boxShadow: "0 2px 8px rgba(40, 167, 69, 0.3)",
                                  }}
                                >
                                  ₹{Number(transaction.amount).toLocaleString('en-IN')}
                                </span>
                              </td>

                              {/* Type Column */}
                              <td className="text-center px-3 py-2" style={{ border: 'none' }}>
                                <span
                                  style={{
                                    display: "inline-block",
                                    padding: "0.45rem 0.9rem",
                                    borderRadius: "999px",
                                    fontSize: "0.8rem",
                                    fontWeight: 600,
                                    lineHeight: 1,
                                    backgroundColor: "transparent",
                                    border: "2px solid #5FD965",
                                    color: "#5FD965",
                                    minWidth: 85,
                                    textAlign: "center",
                                  }}
                                >
                                  REGULAR
                                </span>
                              </td>

                              {/* Date Column */}
                              <td className="text-center px-3 py-2" style={{ border: 'none' }}>
                                <span
                                  className="fw-medium"
                                  style={{
                                    fontSize: "0.85rem",
                                    color: "var(--bs-secondary-color)",
                                  }}
                                >
                                  {new Date(transaction.payment_date).toLocaleDateString('en-IN', {
                                    day: '2-digit',
                                    month: '2-digit',
                                    year: 'numeric'
                                  })}
                                </span>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  ) : (
                    <div className="text-center py-5">
                      <i className="fas fa-inbox fs-1 mb-3" style={{ color: 'var(--bs-secondary-color)' }}></i>
                      <p className="mb-0" style={{ color: 'var(--bs-secondary-color)' }}>No transactions found</p>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Dashboard;
